import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { apiFetch } from '../lib/api.js'
import { GAME_DEFAULTS } from '../lib/constants.js'
import HostHeader from '../components/ui/HostHeader.jsx'
import HostSideNav from '../components/ui/HostSideNav.jsx'

const TOGGLE_OPTIONS = [
  {
    key:     'allow_join',
    title:   'Rejoindre la course',
    descOn:  'Autoriser des joueurs à rejoindre la course en cours de route',
    descOff: 'Impossible de rejoindre la course en cours de route',
  },
  {
    key:     'sons_actifs',
    title:   'Effets sonores',
    descOn:  'Effets sonores sur les bonus et attaques',
    descOff: 'Aucun effet sonore',
  },
  {
    key:     'vitesse_active',
    title:   'Vitesse adaptative',
    descOn:  'V2/V3 activées selon les bonnes réponses consécutives',
    descOff: 'Avancement fixe de 1 case par bonne réponse',
  },
]

// ── Bouton bascule pill-shaped ─────────────────────────────────
function Toggle({ value, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={value}
      onClick={() => onChange(!value)}
      className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
        value ? 'bg-blue-500' : 'bg-white/20'
      }`}
    >
      <span
        className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform duration-200 ${
          value ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  )
}

// ── Ligne paramètre toggle ─────────────────────────────────────
function ToggleRow({ title, descOn, descOff, value, onChange }) {
  return (
    <div className="flex flex-col gap-1.5 px-5 py-4 border-b border-white/5 last:border-0">
      <div className="flex items-center justify-between gap-4">
        <span className="text-white font-medium">{title}</span>
        <Toggle value={value} onChange={onChange} />
      </div>
      <p className="text-white/55 text-sm">{value ? descOn : descOff}</p>
    </div>
  )
}

// ── Page principale ────────────────────────────────────────────
export default function OptionsPage() {
  const { code }  = useParams()
  const navigate  = useNavigate()
  const [game, setGame]               = useState(null)
  const [playerCount, setPlayerCount] = useState(0)
  const [draft, setDraft]             = useState(null)
  const [saving, setSaving]           = useState(false)
  const [saveStatus, setSaveStatus]   = useState(null) // null | 'ok' | 'error'
  const [draftError, setDraftError]   = useState(null)
  const pollRef = useRef(null)

  // Chargement initial
  useEffect(() => {
    apiFetch(`/api/games/${code}`)
      .then(data => {
        setGame(data)
        setDraft({
          timer_max: data.config?.timer_max ?? 30,
          timer_min: data.config?.timer_min ?? 5,
          mode_fin:  data.config?.mode_fin  ?? 'all_finish',
        })
      })
      .catch(() => navigate('/', { replace: true }))
  }, [code])

  // Polling du nombre de joueurs
  useEffect(() => {
    async function poll() {
      try {
        const data = await apiFetch(`/api/games/${code}/players`)
        setPlayerCount(data.players?.filter(p => p.statut !== 'disconnected').length ?? 0)
      } catch {}
    }
    poll()
    pollRef.current = setInterval(poll, 2000)
    return () => clearInterval(pollRef.current)
  }, [code])

  async function handleToggle(key, value) {
    // Mise à jour optimiste
    setGame(prev => prev ? { ...prev, config: { ...prev.config, [key]: value } } : prev)
    try {
      await apiFetch(`/api/games/${code}/config`, {
        method: 'PATCH',
        body: JSON.stringify({ [key]: value }),
      })
    } catch {
      // Rollback si erreur
      setGame(prev => prev ? { ...prev, config: { ...prev.config, [key]: !value } } : prev)
    }
  }

  async function handleSaveDraft() {
    setDraftError(null)
    const timerMax = parseInt(draft.timer_max, 10)
    const timerMin = parseInt(draft.timer_min, 10)

    if (isNaN(timerMax) || isNaN(timerMin)) {
      setDraftError('Valeurs invalides.')
      return
    }
    if (timerMin >= timerMax) {
      setDraftError('Le timer min doit être inférieur au timer max.')
      return
    }

    setSaving(true)
    const prevConfig = game?.config
    try {
      const res = await apiFetch(`/api/games/${code}/config`, {
        method: 'PATCH',
        body: JSON.stringify({
          timer_max: timerMax,
          timer_min: timerMin,
          mode_fin:  draft.mode_fin,
        }),
      })
      setGame(prev => prev ? { ...prev, config: res.config } : prev)
      setSaveStatus('ok')
      setTimeout(() => setSaveStatus(null), 2000)
    } catch {
      // Rollback
      setDraft({
        timer_max: prevConfig?.timer_max ?? 30,
        timer_min: prevConfig?.timer_min ?? 5,
        mode_fin:  prevConfig?.mode_fin  ?? 'all_finish',
      })
      setSaveStatus('error')
      setTimeout(() => setSaveStatus(null), 2000)
    } finally {
      setSaving(false)
    }
  }

  function handleGameUpdate(type, data) {
    if (type === 'end') { navigate('/', { replace: true }); return }
    if (data?.statut) setGame(g => ({ ...g, statut: data.statut }))
  }

  const statut = game?.statut ?? 'playing'
  const config = game?.config ?? {}


  return (
    <div className="h-[100dvh] flex flex-col select-none bg-gray-900">

      <HostHeader
        code={code}
        deckTitle={game?.decks?.titre ?? '…'}
        statut={statut}
        playerCount={playerCount}
        maxPlayers={config.max_joueurs ?? GAME_DEFAULTS.MAX_PLAYERS}
        onGameUpdate={handleGameUpdate}
      />

      <div className="flex flex-1 min-h-0 overflow-y-auto">
        <HostSideNav
          code={code}
          active="options"
          statut={statut}
          deckTitle={game?.decks?.titre ?? ''}
          onGameUpdate={handleGameUpdate}
        />

        <div className="flex-1 flex items-start justify-center p-6 pb-20 sm:pb-6">
          <div className="bg-gray-800 border border-white/10 rounded-2xl w-full max-w-md overflow-hidden">

            {/* Section 1 — Toggles */}
            <div className="text-white/25 text-[10px] uppercase tracking-widest text-center py-3 border-b border-white/5">
              ⚙️ Options de la course
            </div>

            {TOGGLE_OPTIONS.map(opt => (
              <ToggleRow
                key={opt.key}
                title={opt.title}
                descOn={opt.descOn}
                descOff={opt.descOff}
                value={config[opt.key] !== false}
                onChange={val => handleToggle(opt.key, val)}
              />
            ))}

            {/* Section 2 — Paramètres numériques */}
            <div className="text-white/25 text-[10px] uppercase tracking-widest text-center py-3 border-t border-white/10 border-b border-white/5">
              ⏱ Paramètres de course
            </div>

            {draft && (
              <div className="px-5 py-4 flex flex-col gap-4">

                {/* Timer max */}
                <div className="flex flex-col gap-1">
                  <label className="text-white font-medium text-sm">Timer max (secondes)</label>
                  <p className="text-white/45 text-xs">Durée maximale pour répondre — expiration = réponse incorrecte</p>
                  <input
                    type="number"
                    min={5}
                    max={120}
                    value={draft.timer_max}
                    onChange={e => setDraft(d => ({ ...d, timer_max: e.target.value }))}
                    className="mt-1 w-full bg-white/10 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-white/30"
                  />
                </div>

                {/* Timer min */}
                <div className="flex flex-col gap-1">
                  <label className="text-white font-medium text-sm">Timer min (secondes)</label>
                  <p className="text-white/45 text-xs">Durée pendant laquelle le bouton valider reste grisé</p>
                  <input
                    type="number"
                    min={1}
                    max={30}
                    value={draft.timer_min}
                    onChange={e => setDraft(d => ({ ...d, timer_min: e.target.value }))}
                    className="mt-1 w-full bg-white/10 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-white/30"
                  />
                </div>

                {/* Mode fin */}
                <div className="flex flex-col gap-1">
                  <label className="text-white font-medium text-sm">Mode de fin</label>
                  <select
                    value={draft.mode_fin}
                    onChange={e => setDraft(d => ({ ...d, mode_fin: e.target.value }))}
                    className="mt-1 w-full bg-white/10 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-white/30"
                  >
                    <option value="all_finish">Attendre tous les joueurs</option>
                    <option value="first_finish">Arrêter au 1er arrivé</option>
                  </select>
                </div>

                {/* Erreur de validation */}
                {draftError && (
                  <p className="text-red-400 text-xs">{draftError}</p>
                )}

                {/* Bouton Enregistrer */}
                <button
                  onClick={handleSaveDraft}
                  disabled={saving}
                  className={`w-full py-2 rounded-lg text-sm font-medium transition-colors ${
                    saveStatus === 'ok'
                      ? 'bg-green-600 text-white'
                      : saveStatus === 'error'
                        ? 'bg-red-600 text-white'
                        : 'bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-50'
                  }`}
                >
                  {saving
                    ? 'Enregistrement…'
                    : saveStatus === 'ok'
                      ? 'Enregistré ✓'
                      : saveStatus === 'error'
                        ? 'Erreur — réessayer'
                        : 'Enregistrer'}
                </button>

              </div>
            )}

          </div>
        </div>
      </div>

    </div>
  )
}
